/**
 * Lesson Status Badge Helpers
 * Centralized logic for computing and displaying lesson status badges
 */

import React from 'react'
import { Lesson } from '../src/types/lesson'

export type LessonDisplayStatus =
  | 'scheduled'
  | 'today'
  | 'in-progress'
  | 'needs-confirmation'
  | 'completed'
  | 'no-show'
  | 'cancelled'

/**
 * Compute the display status for a lesson based on its fields and current time
 * Priority order (highest wins): Cancelled > Completed/No-show > Needs Confirmation > In Progress > Today > Scheduled
 */
export function getLessonDisplayStatus(lesson: Lesson): LessonDisplayStatus {
  const now = new Date()
  const startAt = new Date(lesson.startAt)
  const endAt = new Date(lesson.endAt)

  // Priority 1: Cancelled (highest priority)
  if (lesson.status === 'CANCELED' || lesson.cancelled) {
    return 'cancelled'
  }

  // Priority 2: Completed or No-show
  if (lesson.status === 'COMPLETED') {
    // Check for no-show (attended explicitly set to false)
    if (lesson.attended === false) {
      return 'no-show'
    }
    return 'completed'
  }

  // Priority 3: Time-based statuses (for upcoming/active lessons)
  // FIXED: Check for both SCHEDULED and CONFIRMED statuses
  // Real lessons use CONFIRMED, but we should handle both
  const isUpcomingLesson =
    lesson.status === 'SCHEDULED' ||
    lesson.status === 'CONFIRMED' ||
    !lesson.status // Handle missing status as upcoming

  if (isUpcomingLesson) {
    // Lesson ended but not marked complete = needs confirmation
    if (endAt < now) {
      return 'needs-confirmation'
    }

    // Lesson is currently happening (between start and end time)
    if (startAt <= now && now <= endAt) {
      return 'in-progress'
    }

    // Lesson is today (same calendar day as now)
    const todayStart = new Date(now)
    todayStart.setHours(0, 0, 0, 0)
    const todayEnd = new Date(now)
    todayEnd.setHours(23, 59, 59, 999)

    if (startAt >= todayStart && startAt <= todayEnd) {
      return 'today'
    }

    // Future lesson (default)
    return 'scheduled'
  }

  // Fallback for any unexpected state
  return 'scheduled'
}

/**
 * Get the JSX badge element for a given status
 * Returns a styled pill badge with appropriate colors for light/dark mode
 *
 * Color System:
 * 🟢 Green → Scheduled, Today, In Progress, Completed (positive/active states)
 * 🟡 Yellow → Needs Confirmation (warning state)
 * 🔴 Red → Cancelled, No-Show (negative/final states)
 */
export function getLessonStatusBadge(
  status: LessonDisplayStatus,
  darkMode: boolean,
): React.ReactElement {
  const badgeConfig: Record<
    LessonDisplayStatus,
    { text: string; className: string }
  > = {
    // 🔴 Negative States (Red)
    cancelled: {
      text: 'Cancelled',
      className: darkMode
        ? 'bg-red-900/30 text-red-300 border border-red-500/30'
        : 'bg-red-100 text-red-800 border border-red-200',
    },
    'no-show': {
      text: 'No-Show',
      className: darkMode
        ? 'bg-red-900/30 text-red-300 border border-red-500/30'
        : 'bg-red-100 text-red-800 border border-red-200',
    },

    // 🟢 Positive States (Green)
    completed: {
      text: 'Completed',
      className: darkMode
        ? 'bg-green-900/30 text-green-300 border border-green-500/30'
        : 'bg-green-100 text-green-800 border border-green-200',
    },
    'in-progress': {
      text: 'In Progress',
      className: darkMode
        ? 'bg-blue-900/30 text-blue-300 border border-blue-500/30'
        : 'bg-blue-100 text-blue-800 border border-blue-200',
    },
    today: {
      text: 'Today',
      className: darkMode
        ? 'bg-green-900/30 text-green-300 border border-green-500/30'
        : 'bg-green-100 text-green-800 border border-green-200',
    },
    scheduled: {
      text: 'Scheduled',
      className: darkMode
        ? 'bg-transparent text-green-400 border border-green-500/50'
        : 'bg-transparent text-green-600 border border-green-500',
    },

    // 🟡 Warning State (Yellow)
    'needs-confirmation': {
      text: 'Needs Confirmation',
      className: darkMode
        ? 'bg-yellow-900/30 text-yellow-300 border border-yellow-500/30'
        : 'bg-yellow-100 text-yellow-800 border border-yellow-200',
    },
  }

  const config = badgeConfig[status]

  return React.createElement(
    'span',
    {
      className: `inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.className}`,
    },
    config.text,
  )
}
